import { useState, useEffect, useCallback } from 'react';

/**
 * Hook personalizado para gerenciar dispositivos de áudio de entrada e saída
 * @returns {Object} Objeto contendo dispositivos, funções de controle e estado
 */

const useAudioDevices = () => {
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState([]);
  const [selectedInputDevice, setSelectedInputDevice] = useState('');
  const [selectedOutputDevice, setSelectedOutputDevice] = useState('');
  const [inputVolume, setInputVolume] = useState(() => {
    return parseFloat(localStorage.getItem('softphone_input_volume')) || 1.0;
  });
  const [outputVolume, setOutputVolume] = useState(() => {
    return parseFloat(localStorage.getItem('softphone_output_volume')) || 1.0;
  });
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const checkSupport = useCallback(() => {
    const hasMediaDevices = !!(
      navigator.mediaDevices && navigator.mediaDevices.enumerateDevices
    );
    const hasSetSinkId = !!HTMLAudioElement.prototype.setSinkId;

    setIsSupported(hasMediaDevices);

    if (!hasMediaDevices) {
      setError('Seu navegador não suporta a seleção de dispositivos de áudio');
    }

    return { hasMediaDevices, hasSetSinkId };
  }, []);

  const enumerateDevices = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.warn('enumerateDevices() não é suportado.');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });

        stream.getTracks().forEach((track) => track.stop());
      } catch (permissionError) {
        console.warn(
          'Permissão de microfone negada, labels dos dispositivos podem não estar disponíveis'
        );
      }

      const devices = await navigator.mediaDevices.enumerateDevices();

      const inputDevices = devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microfone ${device.deviceId.slice(0, 8)}...`,
          groupId: device.groupId
        }));

      const outputDevices = devices
        .filter((device) => device.kind === 'audiooutput')
        .map((device) => ({
          deviceId: device.deviceId,
          label:
            device.label || `Alto-falante ${device.deviceId.slice(0, 8)}...`,
          groupId: device.groupId
        }));

      setAudioInputDevices(inputDevices);
      setAudioOutputDevices(outputDevices);

      if (!selectedInputDevice && inputDevices.length > 0) {
        const defaultInput =
          inputDevices.find((device) => device.deviceId === 'default') ||
          inputDevices[0];
        setSelectedInputDevice(defaultInput.deviceId);
        localStorage.setItem(
          'softphone_selected_input_device',
          defaultInput.deviceId
        );
      }

      if (!selectedOutputDevice && outputDevices.length > 0) {
        const defaultOutput =
          outputDevices.find((device) => device.deviceId === 'default') ||
          outputDevices[0];
        setSelectedOutputDevice(defaultOutput.deviceId);
        localStorage.setItem(
          'softphone_selected_output_device',
          defaultOutput.deviceId
        );
      }
    } catch (err) {
      console.error('Erro ao enumerar dispositivos:', err);
      setError('Erro ao carregar dispositivos de áudio: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedInputDevice, selectedOutputDevice]);

  const loadSavedDevices = useCallback(() => {
    const savedInputDevice = localStorage.getItem(
      'softphone_selected_input_device'
    );
    const savedOutputDevice = localStorage.getItem(
      'softphone_selected_output_device'
    );

    if (savedInputDevice) {
      setSelectedInputDevice(savedInputDevice);
    }

    if (savedOutputDevice) {
      setSelectedOutputDevice(savedOutputDevice);
    }
  }, []);

  const selectInputDevice = useCallback(
    (deviceId) => {
      setSelectedInputDevice(deviceId);
      localStorage.setItem('softphone_selected_input_device', deviceId);
    },
    [audioInputDevices]
  );

  const selectOutputDevice = useCallback(
    (deviceId) => {
      setSelectedOutputDevice(deviceId);
      localStorage.setItem('softphone_selected_output_device', deviceId);
    },
    [audioOutputDevices]
  );

  const setInputVolumeLevel = useCallback((volume) => {
    const normalizedVolume = Math.max(0, Math.min(1, volume));
    setInputVolume(normalizedVolume);
    localStorage.setItem('softphone_input_volume', normalizedVolume.toString());

    if (window.audioContext && window.gainNode)
      window.gainNode.gain.value = normalizedVolume;
  }, []);

  const setOutputVolumeLevel = useCallback((volume) => {
    const normalizedVolume = Math.max(0, Math.min(1, volume));
    setOutputVolume(normalizedVolume);
    localStorage.setItem(
      'softphone_output_volume',
      normalizedVolume.toString()
    );

    const audioElements = document.querySelectorAll('audio, video');
    audioElements.forEach((element) => {
      if (
        element.id === 'mediaElement' ||
        element.classList.contains('softphone-audio')
      ) {
        element.volume = normalizedVolume;
      }
    });
  }, []);

  const applyOutputDevice = useCallback(
    async (audioElement, deviceId = selectedOutputDevice) => {
      if (!audioElement || !deviceId) return false;

      if (typeof audioElement.setSinkId !== 'function') {
        console.warn(
          'setSinkId não é suportado neste navegador para dispositivos de saída'
        );
        return false;
      }

      try {
        await audioElement.setSinkId(deviceId);

        return true;
      } catch (error) {
        console.error('Erro ao aplicar dispositivo de saída:', error);
        return false;
      }
    },
    [selectedOutputDevice]
  );

  const getInputConstraints = useCallback(
    (deviceId = selectedInputDevice) => {
      if (!deviceId) {
        return { audio: true };
      }

      return {
        audio: {
          deviceId: deviceId === 'default' ? undefined : { exact: deviceId },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      };
    },
    [selectedInputDevice]
  );

  useEffect(() => {
    if (!navigator.mediaDevices) return;

    const handleDeviceChange = () => {
      enumerateDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange
      );
    };
  }, [enumerateDevices]);

  useEffect(() => {
    checkSupport();
    loadSavedDevices();
  }, [checkSupport, loadSavedDevices]);

  useEffect(() => {
    if (isSupported) {
      enumerateDevices();
    }
  }, [isSupported, enumerateDevices]);

  const refreshDevices = useCallback(() => {
    enumerateDevices();
  }, [enumerateDevices]);

  const getSelectedInputDevice = useCallback(() => {
    return audioInputDevices.find(
      (device) => device.deviceId === selectedInputDevice
    );
  }, [audioInputDevices, selectedInputDevice]);

  const getSelectedOutputDevice = useCallback(() => {
    return audioOutputDevices.find(
      (device) => device.deviceId === selectedOutputDevice
    );
  }, [audioOutputDevices, selectedOutputDevice]);

  return {
    audioInputDevices,
    audioOutputDevices,
    selectedInputDevice,
    selectedOutputDevice,
    inputVolume,
    outputVolume,
    selectInputDevice,
    selectOutputDevice,
    setInputVolumeLevel,
    setOutputVolumeLevel,
    applyOutputDevice,
    getInputConstraints,
    refreshDevices,
    getSelectedInputDevice,
    getSelectedOutputDevice,
    isSupported,
    isLoading,
    error
  };
};

export default useAudioDevices;
